import React, { useEffect, useState, useCallback } from 'react';
import './FloatingButton.css';
import { Link } from 'react-router-dom';
import {
    createFloatingButtonInstance,
    BUTTON_TYPE_WHATSAPP,
    buildWhatsAppLink,
} from '../../Models/FloatingButton';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';

const BASE_BOTTOM_OFFSET = 20;
const BUTTON_STACK_HEIGHT = 56;
const BUTTON_STACK_GAP = 12;

const getButtonSide = (button) => (button?.position === 'left' ? 'left' : 'right');

const getStackIndex = (buttons, index) => {
    const side = getButtonSide(buttons[index]);
    let visibleOnSide = 0;
    let orderOnSide = 0;

    for (let i = 0; i < buttons.length; i++) {
        if (buttons[i]?.isVisible === false) continue;
        if (getButtonSide(buttons[i]) !== side) continue;
        visibleOnSide++;
        if (i < index) orderOnSide++;
    }

    return visibleOnSide - 1 - orderOnSide;
};

const getPositionInlineStyle = (button, stackIndex) => {
    const baseStyle = button?.style?.FloatingDonateButton || {};
    const bottom = BASE_BOTTOM_OFFSET + stackIndex * (BUTTON_STACK_HEIGHT + BUTTON_STACK_GAP);

    if (button?.position === 'left') {
        return {
            bottom: `${bottom}px`,
            left: baseStyle.left || '20px',
            right: 'auto',
        };
    }
    return {
        bottom: `${bottom}px`,
        right: baseStyle.right || '20px',
        left: 'auto',
    };
};

const getButtonHref = (button) => {
    if (button?.buttonType === BUTTON_TYPE_WHATSAPP) {
        return buildWhatsAppLink(button.phoneNumber, button.helpText);
    }
    return button?.link || '#';
};

const isExternalHref = (href) => /^https?:\/\//i.test(href);

const FloatingButtonLink = ({ href, className, style, children }) => {
    if (isExternalHref(href)) {
        return (
            <a
                href={href}
                className={className}
                style={style}
                target="_blank"
                rel="noopener noreferrer"
            >
                {children}
            </a>
        );
    }

    return (
        <Link to={href} className={className} style={style}>
            {children}
        </Link>
    );
};

const FloatingButton = () => {
    const { i18n } = useTranslation();
    const [buttons, setButtons] = useState([]);
    const [floatingButtonInstance] = useState(createFloatingButtonInstance());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const currentLanguage = i18n.language;

    const loadButtonData = useCallback(async (language) => {
        try {
            setIsLoading(true);
            setError(null);
            await floatingButtonInstance.loadByLanguage(language);
            floatingButtonInstance.renderStyles();
            setButtons(floatingButtonInstance.getButtons());
            setIsLoading(false);
        } catch (err) {
            console.error('Failed to load floating button data:', err);
            setError('Failed to load floating button data');
            setIsLoading(false);
        }
    }, [floatingButtonInstance]);

    useEffect(() => {
        loadButtonData(currentLanguage);
    }, [loadButtonData, currentLanguage]);

    useEffect(() => {
        const handleLanguageChangeEvent = () => {
            loadButtonData(i18n.language);
        };

        i18n.on('languageChanged', handleLanguageChangeEvent);

        return () => {
            i18n.off('languageChanged', handleLanguageChangeEvent);
        };
    }, [i18n, loadButtonData]);

    if (isLoading) {
        return (
            <div className="loading-placeholder">
                <Spinner animation="border" role="status" size="sm" />
            </div>
        );
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    const visibleButtons = buttons.filter((button) => button?.isVisible !== false);

    if (visibleButtons.length === 0) {
        return null;
    }

    return (
        <>
            {buttons.map((button, index) => {
                if (button?.isVisible === false) return null;

                const stackIndex = getStackIndex(buttons, index);
                const iconName = button?.icon;
                const hasIcon = iconName != null && String(iconName).trim() !== '';
                const href = getButtonHref(button);

                return (
                    <FloatingButtonLink
                        key={index}
                        href={href}
                        className={`FloatingDonateButton FloatingDonateButton-${index}`}
                        style={getPositionInlineStyle(button, stackIndex)}
                    >
                        {button?.text}
                        {hasIcon && (
                            <i className={`fa-solid fa-${iconName}`} />
                        )}
                    </FloatingButtonLink>
                );
            })}
        </>
    );
};

export default FloatingButton;
